import { AxiosError } from "axios";

/**
 * Pure classification of a failed axios call, kept free of the datadog /
 * toast / session-storage side effects so it can be unit-tested and reused
 * by feature code (the leaderboard's own error kinds build on it).
 *
 * Order matters: a client-side timeout is ALSO "no response", so it must be
 * recognised before the generic network-error check or the user is told they
 * are offline while their connection is fine and the server is merely slow
 * (GLE-8227).
 */
export const NETWORK_ERROR_MESSAGE = "You are offline. Request failed.";
export const REQUEST_TIMED_OUT_MESSAGE =
  "The request took too long to finish. Please try again.";

export interface AxiosFailureKindI {
  /** The client gave up waiting (axios `timeout`): the server may still be working. */
  isTimedout: boolean;
  /** No response at all and not a timeout: offline, DNS, CORS, aborted navigation. */
  isNetworkError: boolean;
}

const hasTimeoutMessage = (message: unknown) =>
  typeof message === "string" && /timeout/i.test(message);

export const classifyAxiosFailure = (
  e: Partial<AxiosError> | null | undefined
): AxiosFailureKindI => {
  // axios reports a timeout as ETIMEDOUT (transitional.clarifyTimeoutError)
  // or, by default, as ECONNABORTED with a "timeout of Nms exceeded" message.
  // ECONNABORTED alone is not enough: the XHR adapter also uses it for a
  // plain abort ("Request aborted"), which is a network-class failure.
  const isTimedout =
    e?.code === AxiosError.ETIMEDOUT ||
    (e?.code === AxiosError.ECONNABORTED && hasTimeoutMessage(e?.message));

  const isNetworkError = !!e?.isAxiosError && !e?.response && !isTimedout;

  return { isTimedout, isNetworkError };
};

/**
 * The line to show a user for a transport failure, or null when the failure
 * is an HTTP answer (4xx/5xx) or not an axios error at all: those are the
 * caller's to name.
 */
export const transportFailureMessage = (error: unknown): string | null => {
  const { isTimedout, isNetworkError } = classifyAxiosFailure(
    error as Partial<AxiosError> | null
  );

  if (isTimedout) {
    return REQUEST_TIMED_OUT_MESSAGE;
  }

  if (isNetworkError) {
    return NETWORK_ERROR_MESSAGE;
  }

  return null;
};
